<template>
    <flash-top-message-banner />
    <div class="scrolling-header relative !pb-0">
        <div class="w-full pb-9 text-white">
            <div :class="isExpandedHeader ? 'relative mb-3 rounded-xl p-3 pb-6' : ''">
                <div :class="isExpandedHeader ? '' : 'p-3'" class="!z-10 block items-start justify-between sm:flex">
                    <div class="flex w-full justify-end sm:order-2 sm:w-fit">
                        <tooltip-wrapper
                            :class="canUpdateOperability ? 'cursor-pointer' : '!cursor-default'"
                            :enabled="isOperationInProgress || !isAssetOnboarded"
                            :tooltip="trans('asset.action_disabled_due_to_incomplete_onboarding')"
                            class="w-fit items-end"
                            role="button"
                            @click="onOperabilityCardClick"
                        >
                            <card :bodyClasses="['flex', 'gap-2', 'items-center']">
                                <span :class="displayedIsOperable ? 'text-green' : 'text-red'" class="text-sm font-medium">{{
                                    getOperabilityLabel()
                                }}</span>
                                <asset-operability-switch
                                    v-if="canUpdateOperability"
                                    id="header-operability-switch"
                                    :disabled="isOperabilitySwitchDisabled"
                                    :model-value="displayedIsOperable"
                                    @changed="handleOperabilityChange"
                                />
                            </card>
                        </tooltip-wrapper>
                    </div>
                    <div class="sm:order-1">
                        <p
                            data-testid="asset-header-name"
                            :class="isExpandedHeader ? 'break-words whitespace-normal' : 'line-clamp-2'"
                            class="mb-2 text-xl font-bold sm:text-3xl"
                        >
                            {{ asset.name?.replaceAll('/', ' / ') }}
                        </p>
                        <p
                            v-if="locationLabel"
                            data-testid="asset-header-location"
                            :class="isExpandedHeader ? 'break-words whitespace-normal' : 'line-clamp-2'"
                            class="text-l mb-1 flex gap-1 font-bold"
                        >
                            <span :class="isExpandedHeader ? 'break-words' : 'truncate'">{{ locationLabel }}</span>
                        </p>
                        <p
                            data-testid="asset-header-curie-id"
                            :class="isExpandedHeader ? 'break-words whitespace-normal' : 'line-clamp-2'"
                            class="text-sm opacity-90"
                        >
                            Curie ID: {{ asset.curieId || trans('general.not_available') }}
                        </p>
                    </div>
                </div>
                <div
                    v-if="isExpandedHeader"
                    data-testid="asset-header-background"
                    :class="displayedIsOperable ? '!bg-green' : '!bg-red'"
                    class="absolute inset-0 -z-10 rounded-xl"
                ></div>
            </div>
            <template v-if="!isExpandedHeader">
                <div
                    data-testid="asset-header-background"
                    :class="[displayedIsOperable ? '!bg-green' : '!bg-red', '!mb-32']"
                    class="absolute inset-0 -z-10 m-3 rounded-xl"
                ></div>
                <div class="bottom-0 w-full">
                    <div
                        data-testid="asset-header-actions"
                        :class="isMobile ? 'mx-3 !bg-white p-2' : ''"
                        class="grid grid-cols-2 gap-x-2 gap-y-2 rounded-xl xs:grid-cols-4 lg:gap-x-11"
                    >
                        <asset-details-action-card
                            v-for="action in visibleHeaderActions"
                            :key="action.key"
                            :button-id="action.buttonId"
                            :button-label="action.buttonLabel"
                            :feature-name="action.featureName"
                            :href="action.href"
                            :icon-component="action.iconComponent"
                            :is-enabled="isOperationInProgress || !isAssetOnboarded"
                            :permission-name="action.permissionName"
                            :tooltip-text="trans('asset.action_disabled_due_to_incomplete_onboarding')"
                            @created="handleHeaderActionCreated(action, $event)"
                        />
                    </div>
                </div>
            </template>
        </div>
        <tabs :current-route="currentRoute" :tabs="tabs" class="" @tab-click="openTab" />
    </div>
    <confirm-asset-status-change-modal :asset="asset" />
    <suggest-asset-status-change-prompt-modal />
    <blocked-operability-change-modal />
</template>
<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { eventBus, events } from '@/eventBus';
import BlockedOperabilityChangeModal from '@/Pages/Assets/Details/BlockedOperabilityChangeModal.vue';
import ConfirmAssetStatusChangeModal from '@/Pages/Assets/Details/ConfirmAssetStatusChangeModal.vue';
import { isMobile } from '@/Utils/UseMedia';
import Tabs from '@/Components/Tabs.vue';
import Card from '@/Components/Card.vue';
import Workorder from '@/../icons/Workorder.vue';
import Testing from '@/../icons/Testing.vue';
import AssetCheck from '@/../icons/AssetCheck.vue';
import Performance from '@/../icons/Performance.vue';
import AssetOperabilitySwitch from '@/Pages/Assets/Details/AssetOperabilitySwitch.vue';
import { trans } from '@/mixins/i18n.js';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import { hasFeature } from '@/mixins/hasFeature.js';
import TooltipWrapper from '@/Pages/Assets/Details/TooltipWrapper.vue';
import AssetDetailsActionCard from '@/Pages/Assets/Details/AssetDetailsActionCard.vue';
import SuggestAssetStatusChangePromptModal from '@/Pages/Assets/Details/SuggestAssetStatusChangePromptModal.vue';
import { visitModal } from '@inertiaui/modal-vue';
import { buildLocationHierarchyLabel } from '@/Utils/buildLocationHierarchyLabel.ts';
import FlashTopMessageBanner from '@/Components/FlashTopMessageBanner.vue';

const props = defineProps(['activeTab']);
const emit = defineEmits(['workorder-created']);
const page = usePage();

const isOperationInProgress = ref(false);
const displayedOperability = ref(null);
const allTabs = ref([
    {
        route: 'assets.show',
        component: 'icon',
        label: trans('assets.details.tab.details'),
        permissions: ['asset.read'],
    },
    {
        route: 'assets.workorders.index',
        component: 'wrench',
        label: trans('assets.details.tab.workorders'),
        permissions: ['workorder.index'],
        features: ['assetWorkOrderManagement'],
        badge: false,
    },
    {
        route: 'assets.references.index',
        component: 'icon',
        label: trans('assets.details.tab.references'),
        features: ['testRecord', 'performance-data'],
        permissions: ['test.index', 'performance_data.index'],
        badge: false,
    },
    {
        route: 'assets.activities.index',
        component: 'icon',
        label: trans('assets.details.tab.activity_log'),
        permissions: ['asset.activities.index'],
    },
]);

const asset = computed(() => page.props.asset);

const headerActions = computed(() => [
    {
        key: 'workorder',
        buttonId: 'btn-create-workorder',
        buttonLabel: trans('asset.details.action.workorder'),
        href: route('workorders.create', { asset_id: asset.value.id }),
        iconComponent: Workorder,
        featureName: 'asset-work-order-management',
        permissionName: 'workorder.create',
    },
    {
        key: 'testing',
        buttonId: 'btn-record-test',
        buttonLabel: trans('asset.details.action.testing'),
        href: route('tasks.create', {
            asset_id: asset.value.id,
            task_type: 'preventative_maintenance',
        }),
        iconComponent: Testing,
        featureName: 'test-record',
        // All three task tiles open the tasks.create modal, authorized by task.create.
        permissionName: 'task.create',
    },
    {
        key: 'asset-check',
        buttonId: 'btn-create-asset-check',
        buttonLabel: trans('asset.details.action.asset_check'),
        href: route('tasks.create', {
            asset_id: asset.value.id,
            task_type: 'asset_check',
        }),
        iconComponent: AssetCheck,
        featureName: 'asset-status-check',
        permissionName: 'task.create',
    },
    {
        key: 'performance',
        buttonId: 'btn-create-performance',
        buttonLabel: trans('asset.details.action.performance'),
        href: route('tasks.create', {
            asset_id: asset.value.id,
            task_type: 'performance_data',
        }),
        iconComponent: Performance,
        featureName: 'performance-data',
        permissionName: 'task.create',
    },
]);

const visibleHeaderActions = computed(() => {
    return headerActions.value.filter((action) => hasFeature(action.featureName) && hasAnyPermission(action.permissionName));
});

const hasVisibleHeaderActions = computed(() => {
    return visibleHeaderActions.value.length > 0;
});

const isExpandedHeader = computed(() => {
    return !hasVisibleHeaderActions.value;
});

const openWorkOrdersCount = computed(() => {
    return asset.value?.open_work_orders_count ?? 0;
});

const tabs = computed(() => {
    const count = openWorkOrdersCount.value;
    const workOrdersTabTitle = trans('assets.details.tab.workorders');
    const workOrdersLabel = count > 0 ? `${workOrdersTabTitle} (${count})` : workOrdersTabTitle;

    return allTabs.value
        .filter((tab) => {
            const hasFeatures = tab.features ? hasFeature(tab.features) : true;
            return hasFeatures && hasAnyPermission(tab.permissions);
        })
        .map((tab) => (tab.route === 'assets.workorders.index' ? { ...tab, label: workOrdersLabel, badge: count > 0 } : tab));
});

const hasTestIssues = computed(() => {
    return asset.value.tests.issues.length > 0;
});

const hasMaintenanceIssues = computed(() => {
    return asset.value.maintenances.issues.length > 0;
});

const isAssetOnboarded = computed(() => {
    return asset.value.status?.isOnboarded === true;
});

const canUpdateOperability = computed(() => hasAnyPermission('asset.update_operability'));

const isOperabilitySwitchDisabled = computed(() => {
    return isOperationInProgress.value || !isAssetOnboarded.value || !canUpdateOperability.value;
});

const currentRoute = computed(() => {
    return window.route().current();
});

const isOperable = computed(() => {
    return asset.value.status?.operability === 'operable';
});

// Use displayed operability for UI (supports optimistic updates)
const displayedIsOperable = computed(() => {
    if (displayedOperability.value !== null) {
        return displayedOperability.value === 'operable';
    }
    return isOperable.value;
});

// Watch for actual asset changes to sync displayed state
watch(
    () => asset.value.status?.operability,
    (newValue) => {
        displayedOperability.value = null; // Reset to use actual value
    },
);

const openTab = (tab) => {
    router.visit(window.route(tab.route, asset.value));
};

const onTestRecordCreated = (data) => {
    promptUserForAssetStatusChange(data);
    updatedBadges();
};

const handleHeaderActionCreated = (action, data) => {
    if (action.key === 'workorder') {
        emit('workorder-created');
        return;
    }

    if (action.key === 'testing') {
        onTestRecordCreated(data);
    }
};

const updatedBadges = () => {
    // Test records badge
    allTabs.value[1].badge = hasMaintenanceIssues.value;

    // Maintenance Schedule badge
    allTabs.value[2].badge = hasTestIssues.value;
};

const promptUserForAssetStatusChange = (data) => {
    eventBus.$emit(events.updateNotifications);

    if (data.resolution === 'fail' && asset.value.status.operability === 'operable') {
        eventBus.$emit(events.openAssetStatusChangePrompt, {
            ...data,
            asset: asset.value,
        });
    }
};

const getOperabilityLabel = () => {
    const operability = displayedOperability.value !== null ? displayedOperability.value : asset.value.status?.operability;
    if (operability === 'operable') {
        return trans('assets.modal.operability_change.label.operable');
    } else if (operability === 'inoperable') {
        return trans('assets.modal.operability_change.label.inoperable');
    }
    return trans('assets.modal.operability_change.label.unknown');
};
const handleOperabilityChange = (value) => {
    const newOperability = value ? 'operable' : 'inoperable';

    // Set optimistic state for immediate UI feedback
    displayedOperability.value = newOperability;

    // Block returning to operable while non-op corrective work orders are still open.
    const blockingWorkOrders = asset.value.blocking_corrective_work_orders ?? [];
    if (newOperability === 'operable' && blockingWorkOrders.length) {
        eventBus.$emit(events.openBlockedOperabilityChangeModal, { workOrders: blockingWorkOrders });
        return;
    }

    // Open confirmation modal
    if (hasFeature('field_resolvable_issue')) {
        visitModal(route('assets.operability-triage.create', { asset: asset.value.id }));
        return;
    }

    eventBus.$emit(events.openConfirmAssetStatusChangeModal, {
        assetId: asset.value.id,
        operability: newOperability,
    });
};

const onOperabilityCardClick = (event) => {
    if (isOperabilitySwitchDisabled.value) return;

    // Prevent handling when clicking directly on the switch element
    const target = event.target;
    if (target && target.closest && target.closest('.operability-switch')) {
        return;
    }

    // Toggle operability by reusing existing handler (which sets optimistic state)
    handleOperabilityChange(!isOperable.value);
};

const cancelAssetStatusChange = () => {
    // Revert the optimistic update
    displayedOperability.value = null;
};

const locationHierarchy = computed(() => asset.value.location?.hierarchy || []);
const locationLabel = computed(() => buildLocationHierarchyLabel(locationHierarchy.value));

const reloadAsset = () => {
    router.reload({
        only: ['asset'],
        onSuccess: () => {
            displayedOperability.value = null;
        },
    });
};

// Lifecycle hooks
onMounted(() => {
    updatedBadges();

    eventBus.$on(events.assetOperabilityUpdated, reloadAsset);
    eventBus.$on(events.taskApproved, reloadAsset);
    // Keep the asset prop (blocking_corrective_work_orders + open-WO badge) in
    // sync when work orders change on the board, so the block modal doesn't list
    // already-resolved/deleted work orders.
    eventBus.$on(events.workOrderStatusUpdated, reloadAsset);
    eventBus.$on(events.workOrderDeleted, reloadAsset);
    eventBus.$on(events.cancelConfirmAssetStatusChangeModal, cancelAssetStatusChange);
});

onBeforeUnmount(() => {
    eventBus.$off(events.assetOperabilityUpdated, reloadAsset);
    eventBus.$off(events.taskApproved, reloadAsset);
    eventBus.$off(events.workOrderStatusUpdated, reloadAsset);
    eventBus.$off(events.workOrderDeleted, reloadAsset);
    eventBus.$off(events.cancelConfirmAssetStatusChangeModal, cancelAssetStatusChange);
});
</script>
